import {
  Box,
  Grid,
  IconButton,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import CustomTextField from "../../common/CustomTextField";
import {
  OrderInfo,
  useOrderSlice,
  getOrderItemsByOrderId,
  OrderItemDTO,
  fetchOrderDocumentsByIds,
  approveProductReturnRequest,
  rejectProductReturnRequest,
  pendingProductReturnRequest,
} from "../../../redux/slice/order/OrderSlice";
import { useForm } from "react-hook-form";
import {
  CheckCircleOutline,
  Cancel,
  WallpaperOutlined,
  LocalShipping,
  Business,
  CommentOutlined,
  QrCodeScannerOutlined,
  PendingActionsOutlined,
  DescriptionOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import CustomCheckbox from "../../common/CustomCheckBox";
import { tokens } from "../../../theme";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import CustomItemList from "../../common/CustomItemList";
import CustomCommentPopup from "../../common/CustomCommentPopup";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import CustomHelpMessage from "../../common/CustomHelpMessage";
import dayjs from "dayjs";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { ORDER_AVAILABILITY } from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { convertBase64 } from "../../../utils/Utility";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductReturnRequestInfo {
  returnRequestId: number;
  orderId: number;
  productId: number;
  returnReason: string;
  otherReturnReason: string;
  returnDate: string;
  approvalStatus: 'Pending' | 'Approved' | 'Rejected';
  returnRequestComments: string;
  approvalDate: string;
  approvedBy: string;
  orderItems: ReturnOrderItem[];
  attachmentBarcodeSticker: AttachmentInfo[];
  attachmentProductBoxPictures: AttachmentInfo[];
  additionalContext: string;
  returnTrackingInfo: {
    returnCarrier: string;
    returnTracking: string;
  };
  productNotDamaged: boolean;
  productNotTransfer: boolean;
  productStoredInRecommendedEnv: boolean;
  isShippingLabel: boolean;
  signature: string;
  name: string;
}

interface ReturnOrderItem {
  orderItemId: number;
  serialNumber: string;
  size: string;
  units: number;
  productName: string;
  manufacturerName: string;
}

interface AttachmentInfo {
  documentName: string;
  content: string;
  fileType: string;
}

interface ProductReturnRequestDecisionState extends ProductReturnRequestInfo {
  approvalAction: 'approve' | 'reject' | 'pending';
  // UI-only attachment fields. Request-body / API wiring handled later.
  returnLabel?: ExtendedFile[];
  decisionSupportDocument?: ExtendedFile[];
}

interface ProductReturnBundle {
  productReturnId: number;
  productReturnStatus: string;
  returnReason: string;
  otherReturnReason: string;
  additionalContext: string;
  returnDate: string;
  returnTrackingInfo: {
    returnCarrier?: string;
    returnTracking?: string;
  };
  attachmentBarcodeSticker: AttachmentInfo[];
  attachmentProductBoxPictures: AttachmentInfo[];
  returnPendingDocId?: number;
  orderItems: OrderItemDTO[];
}

export interface ReturnRequestRow {
  orderItemId: number;
  serialNumber: string;
  size: string;
  units: number;
  productName: string;
  manufacturerName: string;
  status: string;
  returnDate: string;
  returnReason: string;
  attachments: {
    barcodeSticker: boolean;
    productBoxPictures: boolean;
  };
}


const useReturnRequestForm = () => {
  const form = useForm<ProductReturnRequestDecisionState>();
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const handleItemSelection = (productReturnId: number, allIds: number[]) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productReturnId)) {
        newSet.delete(productReturnId);
      } else {
        newSet.add(productReturnId);
      }
      return newSet;
    });
  };
  const handleSelectAll = (allIds: number[]) => {
    setSelectedItems((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds)
    );
  };
  return { ...form, selectedItems, handleItemSelection, handleSelectAll };
};

const ManageReturnRequest = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    selectedItems,
    handleItemSelection,
    handleSelectAll,
  } = useReturnRequestForm();
  const { navigate } = useNavigation();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    inputFieldSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const outlinedActionBtnSx = (color: string) => ({
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.88rem",
    borderRadius: "10px",
    py: 1.2,
    color,
    borderColor: `${color}66`,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: color,
      backgroundColor: `${color}12`,
      boxShadow: "none",
    },
    "& .MuiTypography-root": { color: `${color} !important` },
    "& .MuiButton-startIcon": { color },
  });
  const dispatch = useAppDispatch();
  const [returnRequest, setReturnRequest] = useState<ProductReturnRequestInfo>();
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });
  const [showPDF, setShowPDF] = useState<{ isShow: boolean; productName: string; fileName: string }>({ isShow: false, productName: "", fileName: "" });
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [commentData, setCommentData] = useState<{ title: string; body: string }>({ title: "", body: "" });
  const { orderItemsListInfo, orderDocumentInfo, status } = useOrderSlice();

  const productReturnBundles = useMemo(() => {
    if (!orderItemsListInfo) return [];
    const bundlesMap = new Map<number, ProductReturnBundle>();
    orderItemsListInfo.forEach((item) => {
      if (item.productReturnId && item.productReturnStatus !== ORDER_AVAILABILITY.RETURNED && item.productReturnStatus !== ORDER_AVAILABILITY.RETURN_REJECTED) {
        if (!bundlesMap.has(item.productReturnId)) {
          bundlesMap.set(item.productReturnId, {
            productReturnId: item.productReturnId,
            productReturnStatus: item.productReturnStatus || 'Pending',
            returnReason: item.returnReason,
            otherReturnReason: item.returnTrackingInfo?.otherReturnReason || '',
            additionalContext: item.returnTrackingInfo?.additionalContext || '',
            returnDate: item.returnedDate || '',
            returnTrackingInfo: {
              returnCarrier: item.returnTrackingInfo?.returnCarrier || '',
              returnTracking: item.returnTrackingInfo?.returnTracking || '',
            },
            attachmentBarcodeSticker: [],
            attachmentProductBoxPictures: [],
            returnPendingDocId: (item.returnTrackingInfo as any)?.returnPendingDocId,
            orderItems: [],
          });
        }
        bundlesMap.get(item.productReturnId)!.orderItems.push(item);
      }
    });
    // Priority order: RETURN_PENDING on top, RETURN_INITIATED below.
    const statusPriority: Record<string, number> = {
      [ORDER_AVAILABILITY.RETURN_PENDING]: 0,
      [ORDER_AVAILABILITY.RETURN_INITIATED]: 1,
    };
    return Array.from(bundlesMap.values()).sort(
      (a, b) =>
        (statusPriority[a.productReturnStatus] ?? 99) -
        (statusPriority[b.productReturnStatus] ?? 99)
    );
  }, [orderItemsListInfo]);

  // The status of the currently selected request(s). Selection is locked to a
  // single status group (see checkbox disabling below), so the first selected
  // item's status represents the whole selection.
  const selectedStatus = useMemo(() => {
    const firstId = Array.from(selectedItems)[0];
    if (firstId === undefined) return null;
    return (
      productReturnBundles.find((b) => b.productReturnId === firstId)
        ?.productReturnStatus ?? null
    );
  }, [selectedItems, productReturnBundles]);

  // "Select All" operates within a single status group. When nothing is selected
  // yet, it defaults to the top-priority group present (PENDING first).
  const hasPendingBundles = productReturnBundles.some(
    (b) => b.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING
  );
  const selectAllTargetStatus =
    selectedStatus ??
    (hasPendingBundles
      ? ORDER_AVAILABILITY.RETURN_PENDING
      : ORDER_AVAILABILITY.RETURN_INITIATED);
  const selectAllGroupIds = productReturnBundles
    .filter((b) => b.productReturnStatus === selectAllTargetStatus)
    .map((b) => b.productReturnId);
  const isAllGroupSelected =
    selectAllGroupIds.length > 0 &&
    selectAllGroupIds.every((id) => selectedItems.has(id));

  // Display label + color for a return status chip.
  const getReturnStatusDisplay = (status?: string) => {
    switch (status) {
      case ORDER_AVAILABILITY.RETURN_PENDING:
        return { label: "Return Pending", color: colors.warning[300] };
      case ORDER_AVAILABILITY.RETURN_INITIATED:
        return { label: "Return Initiated", color: colors.info[300] };
      default:
        return { label: status || "-", color: colors.grey[500] };
    }
  };

  // Keep the form field 'selectAll' in sync with actual selection (within the
  // active status group, since selection is locked to one group).
  useEffect(() => {
    (setValue as unknown as (name: string, value: any) => void)('selectAll', isAllGroupSelected);
  }, [selectedItems, isAllGroupSelected, setValue]);

  useEffect(() => {
    const fetchReturnRequestData = async () => {
      const selectedReturnRequest = location.state?.selectedReturnRequest;
      const orderData: OrderInfo = location.state?.orderInfo;
      
      if (selectedReturnRequest) {
        // Map the selectedReturnRequest data to the expected ProductReturnRequestInfo format
        const requestData: ProductReturnRequestInfo = {
          returnRequestId: selectedReturnRequest.orderId, // Using orderId as returnRequestId
          orderId: selectedReturnRequest.orderId,
          productId: selectedReturnRequest.productId,
          returnReason: "Product Return Request", // Default value since not in the data
          otherReturnReason: selectedReturnRequest.otherReturnReason || "",
          returnDate: selectedReturnRequest.deliveredDate || new Date().toISOString(),
          approvalStatus: 'Pending',
          returnRequestComments: "",
          approvalDate: "",
          approvedBy: "",
          orderItems: selectedReturnRequest.orderItems
            .filter((item: any) => item.productReturnId)
            .map((item: any) => ({
              orderItemId: item.orderItemId,
              serialNumber: item.serialNumber || "",
              size: item.size || "",
              units: item.units || 0,
              productName: item.productName || "",
              manufacturerName: item.manufacturerName || ""
            })),
          attachmentBarcodeSticker: [],
          attachmentProductBoxPictures: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.productBoxPictures)
            .flatMap((item: any) => 
              item.returnTrackingInfo.productBoxPictures.map((docId: number) => ({
                documentName: `Product Box Picture - ${item.serialNumber}`,
                content: "",
                fileType: "image"
              }))
            ),
          additionalContext: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.additionalContext)
            .map((item: any) => item.returnTrackingInfo.additionalContext)
            .join(", "),
          returnTrackingInfo: {
            returnCarrier: selectedReturnRequest.orderItems
              .filter((item: any) => item.returnTrackingInfo?.returnTrackingInfo?.returnCarrier)
              .map((item: any) => item.returnTrackingInfo.returnTrackingInfo.returnCarrier)
              .join(", ") || "",
            returnTracking: selectedReturnRequest.orderItems
              .filter((item: any) => item.returnTrackingInfo?.returnTrackingInfo?.returnTracking)
              .map((item: any) => item.returnTrackingInfo.returnTrackingInfo.returnTracking)
              .join(", ") || ""
          },
          productNotDamaged: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.productNotDamaged !== undefined)
            .every((item: any) => item.returnTrackingInfo.productNotDamaged),
          productNotTransfer: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.productNotTransfer !== undefined)
            .every((item: any) => item.returnTrackingInfo.productNotTransfer),
          productStoredInRecommendedEnv: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.productStoredInRecommendedEnv !== undefined)
            .every((item: any) => item.returnTrackingInfo.productStoredInRecommendedEnv),
          isShippingLabel: false,
          signature: selectedReturnRequest.orderItems
            .filter((item: any) => item.returnTrackingInfo?.signature)
            .map((item: any) => item.returnTrackingInfo.signature)
            .join(", ") || "",
          name: selectedReturnRequest.patientName || ""
        };
        
        setReturnRequest(requestData);
        setInitialValues(requestData);
        if (requestData.orderId) {
          dispatch(getOrderItemsByOrderId(requestData.orderId));
        }
      }
        
       if (orderData) {
         setOrderInfo(orderData);
       } else if (selectedReturnRequest) {
         // Use the selectedReturnRequest directly as orderInfo since it contains all the necessary data
        console.log(selectedReturnRequest)
         setOrderInfo(selectedReturnRequest as OrderInfo);
       }
    };
    
    fetchReturnRequestData();
  }, [location.state, dispatch]);

  const setInitialValues = (requestData: ProductReturnRequestInfo) => {
    setValue('returnRequestId', requestData.returnRequestId);
    setValue('orderId', requestData.orderId);
    setValue('productId', requestData.productId);
    setValue('returnReason', requestData.returnReason);
    setValue('otherReturnReason', requestData.otherReturnReason);
    setValue('returnDate', requestData.returnDate);
    setValue('returnRequestComments', requestData.returnRequestComments);
    setValue('additionalContext', requestData.additionalContext);
    setValue('approvalDate', dayjs().format('MM/DD/YYYY'));
  };



  const onSubmit = (data: ProductReturnRequestDecisionState) => {
    if (selectedItems.size === 0) {
      let errorObj: SingleAlertInfo = {
        message: "Please select at least one product return request to approve or reject.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      return;
    }

    if (data.approvalAction === 'pending') {
      if (!returnLabelFiles?.length) {
        setError("returnLabel", {
          type: "required",
          message: "Return Label is required.",
        });
        return;
      }
      clearErrors("returnLabel");
      setShowOptionsPopup({
        message: `Do you really want to mark selected Product Return Request(s) as Pending?`,
        show: true,
        variant: "confirm",
        title: "Mark Return Request as Pending",
        buttons: [
          {
            name: "Confirm",
            color: 'primary',
            onClick: () => submitPending(data)
          },
          {
            name: "Cancel",
            color: 'secondary',
            onClick: cancelOptionPopup
          }
        ]
      });
      return;
    }

    if (data.approvalAction === 'approve') {
      setShowOptionsPopup({
        message: `Do you really want to approve selected Product Return Request(s)?`,
        show: true,
        variant: "confirm",
        title: "Approve Return Request",
        buttons: [
          { 
            name: "Confirm", 
            color: 'primary', 
            onClick: () => submitApproval(data, 'Approved') 
          }, 
          { 
            name: "Cancel", 
            color: 'secondary', 
            onClick: cancelOptionPopup 
          }
        ]
      });
    } else {
      setShowOptionsPopup({
        // message: `Are you sure you want to reject ${selectedItems.size} selected Product Return Request(s)?`,
        message: `Do you really want to reject selected Product Return Request(s)?`,
        show: true,
        variant: "confirm",
        title: "Reject Return Request",
        buttons: [
          { 
            name: "Confirm", 
            color: 'primary', 
            onClick: () => submitApproval(data, 'Rejected') 
          }, 
          { 
            name: "Cancel", 
            color: 'secondary', 
            onClick: cancelOptionPopup 
          }
        ]
      });
    }
  };

  const submitApproval = async (data: ProductReturnRequestDecisionState, status: 'Approved' | 'Rejected') => {
   
      const firstSelectedItem = Array.from(selectedItems)[0];
      const selectedBundle = productReturnBundles.find(bundle => bundle.productReturnId === firstSelectedItem);
      
      if (!selectedBundle || !orderInfo) {
        let errorObj: SingleAlertInfo = {
          message: "Unable to find order information. Please try again.",
          alertType: "error"
        };
        dispatch(setSingleAlertObj(errorObj));
        return;
      }

      // Optional reference document at reject time (Approval/Rejection Reference).
      // Same shape as the pending document: only content, documentName, fileType.
      let productReturnRejectDocument;
      if (status === 'Rejected') {
        const rejectFile = decisionSupportFiles?.[0];
        if (rejectFile) {
          const base64File = String(await convertBase64(rejectFile));
          const content = base64File.substring(base64File.indexOf(",") + 1);
          productReturnRejectDocument = {
            documentName: rejectFile.name,
            fileType: rejectFile.type,
            content,
          };
        }
      }

      const requestData = {
        productReturnIds: Array.from(selectedItems),
        orderNumber: orderInfo.orderNumber,
        orderId: orderInfo.orderId,
        productId: orderInfo.productId,
        responseStatus: status === 'Approved' ? ORDER_AVAILABILITY.RETURNED : ORDER_AVAILABILITY.RETURN_REJECTED,
        message: data.returnRequestComments || undefined,
        productReturnRejectDocument,
      };

      const result = status === 'Approved'
        ? await dispatch(approveProductReturnRequest(requestData))
        : await dispatch(rejectProductReturnRequest(requestData));

      const isFulfilled = status === 'Approved'
        ? approveProductReturnRequest.fulfilled.match(result)
        : rejectProductReturnRequest.fulfilled.match(result);

      if (isFulfilled) {
        let successObj: SingleAlertInfo = {
          message: status === 'Approved'
            // ? `Order ${orderInfo?.orderNumber} has approved return request successfully.`
            // : `Order ${orderInfo?.orderNumber} has rejected return request successfully.`,
            ? `Return request for Order ${orderInfo?.orderNumber} has been approved.`
            : `Return request for Order ${orderInfo?.orderNumber} has been rejected.`,
          alertType : "success"
        };
        dispatch(setSingleAlertObj(successObj));

        cancelOptionPopup();
        navigate(-1);
      }
  };

  const submitPending = async (data: ProductReturnRequestDecisionState) => {
    const firstSelectedItem = Array.from(selectedItems)[0];
    const selectedBundle = productReturnBundles.find(bundle => bundle.productReturnId === firstSelectedItem);

    if (!selectedBundle || !orderInfo) {
      let errorObj: SingleAlertInfo = {
        message: "Unable to find order information. Please try again.",
        alertType: "error"
      };
      dispatch(setSingleAlertObj(errorObj));
      return;
    }

    // Build the pending document from the uploaded Return Label (single file).
    // Only content, documentName and fileType are sent (per API contract).
    let productReturnPendingDocument;
    const returnLabelFile = returnLabelFiles?.[0];
    if (returnLabelFile) {
      const base64File = String(await convertBase64(returnLabelFile));
      const content = base64File.substring(base64File.indexOf(",") + 1);
      productReturnPendingDocument = {
        documentName: returnLabelFile.name,
        fileType: returnLabelFile.type,
        content,
      };
    }

    // Send all selected returns as an array (single selection => [id]),
    // mirroring how Approve/Reject send their ids.
    const result = await dispatch(pendingProductReturnRequest({
      productReturnIds: Array.from(selectedItems),
      orderId: orderInfo.orderId,
      productReturnPendingDocument,
      message: data.returnRequestComments || undefined,
    }));

    if (pendingProductReturnRequest.fulfilled.match(result)) {
      let successObj: SingleAlertInfo = {
        message: `Return request for Order ${orderInfo?.orderNumber} has been marked as pending.`,
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));

      cancelOptionPopup();
      navigate(-1);
    }
  };

  const onError = () => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  // Reference-document uploads (UI only; request body / API handled later).
  // "Return Label" is required to mark a request as Pending; "Decision Support
  // Document" is optional for Approve/Reject.
  const returnLabelFiles = watch("returnLabel");
  const decisionSupportFiles = watch("decisionSupportDocument");

  const handleReturnLabelUpload = (uploadedFiles: ExtendedFile[]) => {
    setValue("returnLabel", uploadedFiles);
    if (uploadedFiles?.length) {
      clearErrors("returnLabel");
    }
  };

  const handleDecisionSupportUpload = (uploadedFiles: ExtendedFile[]) => {
    setValue("decisionSupportDocument", uploadedFiles);
  };



  const handlePreviewAction = async (attachmentType: 'barcodeSticker' | 'productBoxPictures', bundle: ProductReturnBundle) => {
    try {
      if (attachmentType === 'productBoxPictures' && bundle.orderItems.length > 0) {
        const documentIds = bundle.orderItems
          .flatMap(item => item.returnTrackingInfo?.productBoxPictures || [])
          .filter((id, index, array) => array.indexOf(id) === index);
        
        if (documentIds.length > 0) {
          const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
          const documentData = actionResult.payload;
          
          if (documentData && documentData.content) {
             setShowPDF({
               isShow: true,
               productName: `${orderInfo?.productName || 'Product'} - Product Packaging Image`,
               fileName: `PRODUCT_PACKAGING_IMAGES_${orderInfo?.orderNumber}_${bundle.productReturnId}`,
             });
          }
        } else {
          setShowOptionsPopup({
            message: "No product box pictures found for this return request.",
            show: true,
            variant: "info",
            title: "Information",
            buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
            obj: {}
          });
        }
      } else if (attachmentType === 'barcodeSticker' && bundle.orderItems.length > 0) {
        const documentIds = bundle.orderItems
          .flatMap(item => item.returnTrackingInfo?.barcodes || [])
          .filter((id, index, array) => array.indexOf(id) === index);
        
        if (documentIds.length > 0) {
          const actionResult = await dispatch(fetchOrderDocumentsByIds(documentIds));
          const documentData = actionResult.payload;
          
          if (documentData && documentData.content) {
            setShowPDF({
              isShow: true,
              productName: `${orderInfo?.productName || 'Product'} - Barcode Stickers`,
              fileName: `BARCODE_STICKERS_${orderInfo?.orderNumber}_${bundle.productReturnId}`,
            });
          } else {
            setShowOptionsPopup({
              message: "No document content found for the selected attachment.",
              show: true,
              variant: "info",
              title: "Information",
              buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
              obj: {}
            });
          }
        } else {
          setShowOptionsPopup({
            message: "No barcode stickers found for this return request.",
            show: true,
            variant: "info",
            title: "Information",
            buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
            obj: {}
          });
        }
      }
    } catch (error) {
      console.error('Error fetching attachment:', error);
      setShowOptionsPopup({
        message: "Error loading attachment. Please try again.",
        show: true,
        variant: "error",
        title: "Error",
        buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
        obj: {}
      });
    }
  };

  // Preview the document uploaded while marking the return as Pending
  // (fetched via the returnPendingDocId returned in the order item response).
  const handlePreviewPendingDocument = async (bundle: ProductReturnBundle) => {
    try {
      if (!bundle.returnPendingDocId) {
        setShowOptionsPopup({
          message: "No pending document found for this return request.",
          show: true,
          variant: "info",
          title: "Information",
          buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
          obj: {}
        });
        return;
      }
      const actionResult = await dispatch(fetchOrderDocumentsByIds([bundle.returnPendingDocId]));
      const documentData = actionResult.payload;
      if (documentData && documentData.content) {
        setShowPDF({
          isShow: true,
          productName: `${orderInfo?.productName || 'Product'} - Return Label`,
          fileName: `RETURN_LABEL_${orderInfo?.orderNumber}_${bundle.productReturnId}`,
        });
      } else {
        setShowOptionsPopup({
          message: "No document content found for the selected attachment.",
          show: true,
          variant: "info",
          title: "Information",
          buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
          obj: {}
        });
      }
    } catch (error) {
      console.error('Error fetching pending document:', error);
      setShowOptionsPopup({
        message: "Error loading document. Please try again.",
        show: true,
        variant: "error",
        title: "Error",
        buttons: [{ name: "OK", color: 'secondary', onClick: cancelOptionPopup }],
        obj: {}
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return colors.warning[300];
      case 'approved':
        return colors.success[300];
      case 'rejected':
        return colors.error[300];
      default:
        return colors.grey[500];
    }
  };

  const showAdditionalContextPopup = (bundle: ProductReturnBundle) => {
    const title = "Additional Context";
    const body = bundle.additionalContext || "No additional context provided.";
    setCommentData({ title, body });
    setShowCommentPopup(true);
  };

  const hideAdditionalContextPopup = () => {
    setShowCommentPopup(false);
  };


  const columns = [
    { id: "serialNumber", label: "Serial Number", minWidth: 150, sticky: true },
    { id: "size", label: "Size", minWidth: 120, sticky: true },
  ];

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: mTokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, productName: "", fileName: "" })
          }
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={orderDocumentInfo?.content || ""}
        />
      )}

      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}

      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} />
        <Box data-testid="header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Orders / Manage Return Request
          </Typography>
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Manage Return Request
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            Review the return request details and record your decision.
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
          {/* Left: return requests, uploads, comments, actions */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 2, md: 1 } }}>

            {/* Card 1: Return Requests */}
            <Box sx={cardSx}>
              <Box sx={{ px: 2.8, pt: 1.8, pb: 2.8 }}>
                {productReturnBundles.length > 0 ? (
                  <>
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                      <CustomCheckbox
                        control={control}
                        controllerName="selectAll"
                        checked={isAllGroupSelected}
                        onChange={() => handleSelectAll(selectAllGroupIds)}
                      />
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: mTokens.textPrimary }}>
                        Select All ({selectedItems.size} of {selectAllGroupIds.length} selected)
                        {" "}
                        <Typography component="span" sx={{ fontSize: "0.78rem", color: getReturnStatusDisplay(selectAllTargetStatus).color }}>
                          ({getReturnStatusDisplay(selectAllTargetStatus).label})
                        </Typography>
                      </Typography>
                    </Box>
                    {productReturnBundles.map((bundle, bundleIndex) => (
                      <Box
                        key={bundle.productReturnId}
                        sx={{
                          mb: 1.8,
                          borderRadius: "12px",
                          border: `1px solid ${mTokens.border}`,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 1.8,
                            py: 1,
                            borderBottom: `1px solid ${mTokens.border}`,
                            backgroundColor: mTokens.headerFooterBg,
                          }}
                        >
                          <CustomCheckbox
                            control={control}
                            checked={selectedItems.has(bundle.productReturnId)}
                            isDisabled={selectedStatus !== null && bundle.productReturnStatus !== selectedStatus}
                            tooltipMessage={
                              selectedStatus !== null && bundle.productReturnStatus !== selectedStatus
                                ? "You can only select requests with the same status at a time."
                                : ""
                            }
                            onChange={() => handleItemSelection(bundle.productReturnId, productReturnBundles.map(b => b.productReturnId))}
                          />
                          <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: mTokens.textPrimary }}>
                            Request #{bundleIndex + 1}
                          </Typography>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.25,
                              borderRadius: 5,
                              border: `1px solid ${getReturnStatusDisplay(bundle.productReturnStatus).color}`,
                              color: getReturnStatusDisplay(bundle.productReturnStatus).color,
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold">
                              {getReturnStatusDisplay(bundle.productReturnStatus).label}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ p: 1.8, backgroundColor: mTokens.bg }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  bgcolor: mTokens.headerFooterBg,
                                  border: `1px solid ${mTokens.border}`,
                                  borderRadius: "10px",
                                  p: 2,
                                  position: "relative",
                                }}
                              >
                                <Box display="flex" flexDirection="column" gap={2}>
                                  {/* Return Reason Row */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, minWidth: 120 }}>
                                      Return Reason
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, justifyContent: "flex-end" }}>
                                      <Typography sx={{ fontSize: "0.86rem", color: mTokens.textPrimary, fontWeight: 600 }}>
                                        {(() => {
                                          const returnReason = bundle.returnReason || 'Not specified';
                                          const otherReason = bundle.otherReturnReason;
                                          if (otherReason && otherReason.trim() !== '') {
                                            return `${returnReason} - ${otherReason}`;
                                          }
                                          return returnReason;
                                        })()}
                                      </Typography>
                                      {bundle.additionalContext && (
                                        <CustomIconButton
                                          tooltipPlacement="top"
                                          tooltipMessage="Comment Return product"
                                          color="info"
                                          size="small"
                                          variant="outlined"
                                          onClick={() => showAdditionalContextPopup(bundle)}
                                        >
                                          <CommentOutlined fontSize="small" />
                                        </CustomIconButton>
                                      )}
                                    </Box>
                                  </Box>

                                  {/* Return Date Row */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, minWidth: 120 }}>
                                      Return Date
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.86rem", color: mTokens.textPrimary, fontWeight: 600 }}>
                                      {bundle.returnDate ? dayjs(bundle.returnDate).format('MM/DD/YYYY') : 'Not specified'}
                                    </Typography>
                                  </Box>

                                  {/* Attachments Row */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, minWidth: 120 }}>
                                      Attachments
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, justifyContent: "flex-end" }}>
                                      {(() => {
                                        const hasBarcodes = bundle.orderItems.length > 0 &&
                                          bundle.orderItems.some(item => item.returnTrackingInfo?.barcodes && item.returnTrackingInfo.barcodes.length > 0);
                                        const hasProductBoxPictures = bundle.orderItems.length > 0 &&
                                          bundle.orderItems.some(item => item.returnTrackingInfo?.productBoxPictures && item.returnTrackingInfo.productBoxPictures.length > 0);

                                        return (
                                          <>
                                            {hasBarcodes ? (
                                              <CustomIconButton
                                                tooltipPlacement="top"
                                                tooltipMessage="Barcode Stickers"
                                                color="info"
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handlePreviewAction('barcodeSticker', bundle)}
                                              >
                                                <QrCodeScannerOutlined fontSize="small" />
                                              </CustomIconButton>
                                            ) : null}
                                            {hasProductBoxPictures ? (
                                              <CustomIconButton
                                                tooltipPlacement="top"
                                                tooltipMessage="Product Packaging Image"
                                                color="info"
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handlePreviewAction('productBoxPictures', bundle)}
                                              >
                                                <WallpaperOutlined fontSize="small" />
                                              </CustomIconButton>
                                            ) : null}
                                            {!hasBarcodes && !hasProductBoxPictures ? (
                                              <Typography sx={{ fontSize: "0.86rem", color: mTokens.textSecondary }}>
                                                None
                                              </Typography>
                                            ) : null}
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  </Box>
                                  {bundle.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING && bundle.returnPendingDocId ? (
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, minWidth: 120 }}>
                                        Return Label
                                      </Typography>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, justifyContent: "flex-end" }}>
                                        <CustomIconButton
                                          tooltipPlacement="top"
                                          tooltipMessage="View Return Label"
                                          color="info"
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handlePreviewPendingDocument(bundle)}
                                        >
                                          <DescriptionOutlined fontSize="small" />
                                        </CustomIconButton>
                                      </Box>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <CustomItemList
                                columns={columns}
                                rows={bundle.orderItems.map((item) => ({
                                  serialNumber: item.serialNumber || '',
                                  size: item.size || '',
                                }))}
                                maxHeight={200}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', color: mTokens.textSecondary }}>
                    <Typography>No product return requests found for this order.</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Attachments + Comments + actions */}
            <Box sx={cardSx}>
              <Box sx={{ px: 2.8, pt: 1.8, pb: 2.8 }}>
                <Grid container spacing={2.5}>
                  <Grid item md={6} xs={12}>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: mTokens.textPrimary }}>
                        Return Label
                      </Typography>
                      <CustomHelpMessage
                        title="Help"
                        message="Upload the return label. This is required to mark the return request as Pending."
                      />
                    </Box>
                    <Dropzone
                      name="returnLabel"
                      control={control}
                      onFileChange={() => {}}
                      onFileChangeTest={(uploadedFiles) => handleReturnLabelUpload(uploadedFiles)}
                      holdOnChange={true}
                    />
                    {(errors as any)?.returnLabel && (
                      <Typography sx={{ color: mTokens.error, fontSize: '12px', mt: 0.5 }}>
                        {(errors as any).returnLabel?.message || "Return Label is required."}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: mTokens.textPrimary }}>
                        Approval/Rejection Reference{" "}
                        <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 700, color: mTokens.textPrimary }}>
                          [Optional]{" "}
                        </Typography>
                      </Typography>
                      <CustomHelpMessage
                        title="Help"
                        message="Upload the final decision reference document. Optional for Approve/Reject."
                      />
                    </Box>
                    <Dropzone
                      name="decisionSupportDocument"
                      control={control}
                      onFileChange={() => {}}
                      onFileChangeTest={(uploadedFiles) => handleDecisionSupportUpload(uploadedFiles)}
                      holdOnChange={true}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2.5 }}>
                <FieldLabel label="Comments" />
                <Box sx={inputFieldSx()}>
                  <CustomTextField
                    controllerName="returnRequestComments"
                    control={control}
                    label=""
                    multiline
                    rows={4}
                    maxRows={4}
                  />
                </Box>

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  justifyContent={{ xs: "stretch", sm: "flex-end" }}
                  gap={1.2}
                  mt={2.5}
                >
                  <Button
                    type="submit"
                    variant="outlined"
                    removeUpperCase
                    startIcon={<PendingActionsOutlined />}
                    onClick={() => { clearErrors('returnLabel'); setValue('approvalAction', 'pending'); }}
                    disabled={selectedItems.size === 0 || selectedStatus === ORDER_AVAILABILITY.RETURN_PENDING}
                    sx={{ ...outlinedActionBtnSx("#F59E0B"), width: { xs: "100%", sm: "auto" } }}
                  >
                    Mark As Pending
                  </Button>
                  <Button
                    type="submit"
                    variant="outlined"
                    removeUpperCase
                    startIcon={<CheckCircleOutline />}
                    onClick={() => { clearErrors('returnLabel'); setValue('approvalAction', 'approve'); }}
                    disabled={selectedItems.size === 0}
                    sx={{ ...outlinedActionBtnSx("#10B981"), width: { xs: "100%", sm: "auto" } }}
                  >
                    Approve
                  </Button>
                  <Button
                    type="submit"
                    variant="outlined"
                    removeUpperCase
                    startIcon={<Cancel />}
                    onClick={() => { clearErrors('returnLabel'); setValue('approvalAction', 'reject'); }}
                    disabled={selectedItems.size === 0}
                    sx={{
                      ...cancelBtnSx,
                      width: { xs: "100%", sm: "auto" },
                      "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                      "& .MuiButton-startIcon": { color: mTokens.error },
                    }}
                  >
                    Reject
                  </Button>
                </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right: order info */}
          <Box sx={{ ...cardSx, order: { xs: 1, md: 2 } }}>
            <SectionHeader icon={<PersonOutlined fontSize="small" />} title="Order Info" />
            <Box sx={{ px: 2.8, py: 1.5 }}>
              <InfoRow label="Product Name" value={orderInfo?.productName || "-"} />
              <InfoRow label="Order Number" value={orderInfo?.orderNumber || "-"} />
              <InfoRow label="Order Date" value={orderInfo?.orderDate ? dayjs(orderInfo.orderDate).format('MM/DD/YYYY') : "-"} />
              <InfoRow label="Patient Name" value={orderInfo?.patientName || "-"} />

              <Divider sx={{ my: 1, borderColor: mTokens.border }} />

              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Business sx={{ fontSize: "1rem", color: mTokens.textSecondary }} />
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: mTokens.textSecondary }}>
                  Practice Details
                </Typography>
              </Box>
              <InfoRow label="Practice Name" value={orderInfo?.practiceName || "-"} />
              <InfoRow
                label="Provider"
                value={orderInfo?.orderInfo?.provider || orderInfo?.providerName || orderInfo?.orderInfo?.providerName || "-"}
              />

              <Divider sx={{ my: 1, borderColor: mTokens.border }} />

              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LocalShipping sx={{ fontSize: "1rem", color: mTokens.textSecondary }} />
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: mTokens.textSecondary }}>
                  Shipping Details
                </Typography>
              </Box>
              <InfoRow label="Carrier" value={orderInfo?.shippingCarrier || "-"} />
              <InfoRow label="Tracking #" value={orderInfo?.trackingNumber || "-"} />
            </Box>
          </Box>
        </Box>
      </form>

      <CustomCommentPopup
        open={showCommentPopup}
        onClose={hideAdditionalContextPopup}
        title={commentData.title}
        commentBody={commentData.body}
      />
    </Box>
  );
};

export default ManageReturnRequest;